
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';

import { sharedState } from '../../../../../framework/state';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import TextField from '../../../../../framework/fields/TextField.vue';
import SwitchField from '../../../../../framework/fields/SwitchField.vue';
import DateField from '../../../../../framework/fields/DateField.vue';
import { getResource, getResourcesNew, putResource } from '../../../../../framework/client/resource';
import { getEnumerationOptions } from '../../../../../framework/service/options';

import PurchaseInvoiceRows from './row/PurchaseInvoiceRows.vue';
import { getVendorOptions } from '../../../../service/application_options_service';
import { errorToast, successToast } from '../../../../service/toast_service';
import { updateCachedResourceRow } from '../../../../service/query_cache_service';
import { VendorType } from '../../../../../../common/application/model/enums/VendorType';
import { PurchaseInvoice } from '../../../../../../common/application/model/PurchaseInvoice';
import { PurchaseInvoiceState } from '../../../../../../common/application/model/enums/PurchaseInvoiceState';
import { PurchaseInvoiceRow } from '../../../../../../common/application/model/PurchaseInvoiceRow';
import { PageSize } from '../../../../../../common/application/model/enums/PageSize';
import { Finvoice } from '../../../../../../common/application/model/integration/finvoice/Finvoice';
import {
    getFinvoiceByInvoiceId,
    getInvoiceRowsTotalPrice,
    getNetvisorPurchaseInvoiceAttachments,
    setOpenApprovedIfEligible,
} from '../../../../client/purchase_invoice_client';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { FileMeta } from '../../../../../../common/application/model/FileMeta';
import { BLoadingComponent } from 'buefy/types/components';
import { Vendor } from '../../../../../../common/application/model/Vendor';
import { RegistrationNumberMatch } from '../../../../../../common/application/model/integration/finvoice/RegistrationNumberMatch';

import RegistrationNumberMatchSelect from '../../../../fields/RegistrationNumberMatchSelect.vue';
import { ApplicationUserRole } from '../../../../../../common/application/enumeration/ApplicationUserRole';
import { formatCurrency } from '../../../../service/helper_service';
import { P_PAGE_SIZE } from '../../../../../../common/framework/constants';

@Component({
    components: {
        SelectField,
        TextField,
        SwitchField,
        DateField,
        PurchaseInvoiceRows,
        RegistrationNumberMatchSelect,
        ValidationObserver,
    },
})
export default class EditPurchaseInvoice extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.PURCHASE_INVOICE;

    // Properties
    @Prop(String) readonly id!: string;

    // State
    shared = sharedState;
    local = {
        row: {} as PurchaseInvoice,
        purchaseInvoiceRows: [] as PurchaseInvoiceRow[],
        purchaseInvoiceStateOptions: [] as { id: string | undefined; label: string }[],
        vendorOptions: [] as { id: string | undefined; label: string }[],
        vendorTypeOptions: [] as { id: string | undefined; label: string }[],
        finvoice: {} as Finvoice,
        oldInvoiceState: PurchaseInvoiceState.UNSPECIFIED as PurchaseInvoiceState,
        attachments: [] as FileMeta[],
        registrationNumberMatches: [] as RegistrationNumberMatch[],
        loadingComponent: {} as BLoadingComponent,
        invoiceRowsTotalPrice: 0,
    };

    // Functions
    async mounted() {
        this.showLoading();

        this.local.vendorOptions = await getVendorOptions();
        this.local.vendorTypeOptions = getEnumerationOptions('VendorType', Object.keys(VendorType), this);
        this.local.row = (await getResource<PurchaseInvoice>(this.resourceType, this.id))!!;

        // Accepted only ever comes from Netvisor (the server rejects it too), so it is offered only to
        // show an invoice that already is accepted.
        this.local.purchaseInvoiceStateOptions = getEnumerationOptions(
            'PurchaseInvoiceState',
            Object.keys(PurchaseInvoiceState).filter(
                (key) => key !== 'ACCEPTED' || this.local.row.purchaseInvoiceState === PurchaseInvoiceState.ACCEPTED,
            ),
            this,
        );

        await this.loadPurchaseInvoiceRows();

        if (
            this.shared.admin ||
            this.shared.hasRole(ApplicationUserRole.MANAGER) ||
            this.shared.hasRole(ApplicationUserRole.MANAGER)
        ) {
            const result = await getFinvoiceByInvoiceId(this.id);
            this.local.finvoice = result.finvoice;
            this.local.registrationNumberMatches = result.registrationNumbers.filter(
                (match) => match.rowNumber === 'invoice',
            );
        }

        if (this.local.row.externalKey) {
            await this.loadAttachments();
        }

        this.local.oldInvoiceState = this.local.row.purchaseInvoiceState;

        this.local.invoiceRowsTotalPrice = await getInvoiceRowsTotalPrice(this.local.row.id);

        if (
            this.local.row &&
            this.local.row.purchaseInvoiceState !== PurchaseInvoiceState.OPEN_APPROVED &&
            this.local.row.purchaseInvoiceState !== PurchaseInvoiceState.ACCEPTED && 
            this.local.row.purchaseInvoiceState !== PurchaseInvoiceState.OPEN_APPROVAL
        ) {
            try {
                const isOpenApproved = await setOpenApprovedIfEligible(this.local.row.id);
                if (isOpenApproved) {
                    this.local.row = (await getResource<PurchaseInvoice>(this.resourceType, this.id))!!;
                }
            } catch (e) {
                console.warn('Auto-approve check failed', e);
            }
        }

        this.hideLoading();
    }

    @Watch('local.row.vendorId')
    async imageIdChange(selectedVendorId: string | undefined) {
        if (!selectedVendorId) {
            return;
        }

        const vendor = await getResource<Vendor>(ApplicationResource.VENDOR, selectedVendorId);

        if (!vendor) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            vendorName: vendor.name,
            vendorType: vendor.vendorType,
            vendorNumber: vendor.vendorNumber,
        };
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    back() {
        this.$router.go(-1);
    }

    fileUrl(attachment: FileMeta): string {
        return `/api/user/netvisor/purchaseinvoice/attachments/${attachment.resourceId}_${attachment.name}`;
    }

    save() {
        if (!this.canBeSaved) {
            return false;
        }

        this.saveResource(false);
    }

    saveAndClose() {
        if (!this.canBeSaved) {
            return false;
        }

        this.saveResource(true);
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }

    private async loadPurchaseInvoiceRows(): Promise<void> {
        const parameters = new Map<string, string>();
        parameters.set('purchaseInvoiceId', this.id);
        parameters.set(P_PAGE_SIZE, PageSize.L.toString());

        this.local.purchaseInvoiceRows = [];
        this.local.purchaseInvoiceRows = await getResourcesNew<PurchaseInvoiceRow>(
            'purchase-invoice-row',
            0,
            parameters,
        );
    }

    private async saveResource(goBack: boolean) {
        this.showLoading();

        try {
            if (
                this.local.row &&
                this.local.row.purchaseInvoiceState !== PurchaseInvoiceState.OPEN_APPROVED &&
                this.local.row.purchaseInvoiceState !== PurchaseInvoiceState.ACCEPTED && 
                this.local.row.purchaseInvoiceState !== PurchaseInvoiceState.OPEN_APPROVAL
            ) {
                const isOpenApproved = await setOpenApprovedIfEligible(this.local.row.id);
                if (isOpenApproved) {
                    this.local.row = (await getResource<PurchaseInvoice>(this.resourceType, this.id))!!;
                }
            }
            const updatedRow = await putResource<PurchaseInvoice>(this.resourceType, this.id, this.local.row);
            await this.fetchAttachmentsFromNetvisor(updatedRow);

            successToast(this, 'message.purchaseInvoiceSaved');
            updateCachedResourceRow<PurchaseInvoice>(updatedRow, this.id, this.resourceType);

            if (goBack) {
                this.back();
            }
        } catch (error) {
            errorToast(this);
        }

        this.hideLoading();
    }

    private get canBeSaved(): boolean {
        if (
            this.local.row.purchaseInvoiceState !== PurchaseInvoiceState.OPEN_APPROVED &&
            this.local.row.purchaseInvoiceState !== PurchaseInvoiceState.OPEN_TARGETED &&
            this.local.row.purchaseInvoiceState !== PurchaseInvoiceState.REJECTED
        ) {
            return true;
        }

        let missingVehicleContract = false;
        let missingRowType = false;

        for (const row of this.local.purchaseInvoiceRows) {
            if (!row.vehicleContractId) {
                missingVehicleContract = true;
            }

            if (!row.invoiceRowType) {
                missingRowType = true;
            }
        }

        if (missingVehicleContract || missingRowType) {
            errorToast(this, 'message.missingInvoiceRowInfo');
            return false;
        } else {
            return true;
        }
    }

    private async fetchAttachmentsFromNetvisor(updatedRow: PurchaseInvoice): Promise<void> {
        if (
            updatedRow.purchaseInvoiceState !== PurchaseInvoiceState.ACCEPTED &&
            updatedRow.purchaseInvoiceState !== PurchaseInvoiceState.OPEN_APPROVED &&
            updatedRow.purchaseInvoiceState !== PurchaseInvoiceState.OPEN_TARGETED
        ) {
            return;
        }

        if (this.local.oldInvoiceState === updatedRow.purchaseInvoiceState) {
            return;
        }

        await getNetvisorPurchaseInvoiceAttachments(this.local.row.id);
        await this.loadAttachments();
        this.local.oldInvoiceState = updatedRow.purchaseInvoiceState;
    }

    private async loadAttachments(): Promise<void> {
        const parameters = new Map<string, string>().set('resourceId', this.id);
        this.local.attachments = await getResourcesNew<FileMeta>(ApplicationResource.FILE_META, -1, parameters);
    }

    private showLoading(): void {
        this.local.loadingComponent = this.$buefy.loading.open({});
    }

    private hideLoading(): void {
        this.local.loadingComponent.close();
    }
}
